<script setup lang="ts">
import { computed, ComputedRef, onMounted, ref } from 'vue';
import AddressEditor from '@/components/AddressEditor';
import { Commands, Models, useRegistry, useLocalization, ViewContexts } from '@hantera/portal-app';
import { ToggleButton, useClose, TextInput } from '@hantera/design-system';

const props = defineProps<{
  orderViewContext: ViewContexts.OrderViewContext
  address: Models.Graph.Address
  isInvoiceRecipient: boolean
}>();

const close = useClose();
const addressEditor = ref();
const t = useLocalization();
const registry = useRegistry();
const channelCountries = ref<string[] | undefined>(undefined);

const taxId = ref(props.orderViewContext.state.invoiceRecipient.taxId)

async function loadChannelCountries() {
  const channelKey = (props.orderViewContext.state as any).channelKey
  if (!channelKey) return
  try {
    const channelData = await registry.get(`channels/${channelKey}`)
    if (channelData?.countries && Object.keys(channelData.countries).length > 0) {
      channelCountries.value = Object.keys(channelData.countries)
    }
  } catch {
    // Channel not in registry
  }
}

loadChannelCountries()
const assignTo = ref<{
  delivery: Models.Graph.Delivery
  assign: any
}[]>([]);
const editingAddress = ref(props.address);
const assignToInvoiceRecipient = ref(props.isInvoiceRecipient);

const assignCount = computed(() => {
  const deliveryCount = assignTo.value.reduce((res, a) => a.assign ? res + 1 : res, 0);

  if (assignToInvoiceRecipient.value) {
    return deliveryCount + 1;
  } else {
    return deliveryCount;
  }
});
const disableSave = computed(() => assignCount.value === 0);

onMounted(() => {
  assignTo.value = props.orderViewContext.state.deliveries.map((d: Models.Graph.Delivery) => {

    let assignValue: boolean | ComputedRef<boolean> = props.orderViewContext.state.deliveries.findIndex((r: Models.Graph.Delivery) => r.deliveryId === d.deliveryId) >= 0;
    if (d.deliveryState !== 'open') {
      assignValue = computed(() => objectsAreEqual(editingAddress.value, props.address));
    }

    return {
      delivery: d,
      assign: assignValue
    };
  });
});

async function save() {
  if (!addressEditor.value || !addressEditor.value.validate()) {
    return;
  }

  const assignToDeliveryNumbers = assignTo.value.filter(r => r.delivery.deliveryState === 'open' && r.assign).map(r => r.delivery.deliveryNumber);
  const messageParts: string[] = [];
  if (assignToInvoiceRecipient.value) {
    messageParts.push('invoice address');
  }
  if (assignToDeliveryNumbers.length > 0) {
    messageParts.push('address of deliveries: {deliveryNumbers}');
  }

  const commands = [] as Commands.Order.OrderCommand[];

  assignTo.value.filter(r => r.assign).map(r => r.delivery).forEach(d => {
    commands.push({
      ...editingAddress.value,
      type: 'setDeliveryAddress',
      deliveryId: d.deliveryId
    });
  });

  if (assignToInvoiceRecipient.value) {
    commands.push({
      type: 'setInvoiceRecipient',
      ...editingAddress.value,
      taxId: taxId.value
    });
  }

  await props.orderViewContext.pushPendingChange(
    undefined,
    commands,
    `Set ${messageParts.join(' and ')}`,
    {
      deliveryNumbers: assignTo.value.filter(r => r.assign).map(r => r.delivery.deliveryNumber).join(', '),
    }
  )

  close();
}

function objectsAreEqual(a: any, b: any): boolean {
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);

  if (keysA.length !== keysB.length) {
    return false;
  }

  for (const key of keysA) {
    if (a[key] !== b[key]) {
      return false;
    }
  }

  return true;
}

</script>

<template>
  <div class="order-address__editor-container">
    <AddressEditor
      v-model="editingAddress"
      ref="addressEditor"
      :allowed-countries="channelCountries"
    >
      <template v-if="assignToInvoiceRecipient">
        <div class="field">
          <label>Tax ID</label>
          <TextInput v-model="taxId" />
        </div>
      </template>    
    </AddressEditor>
    

    <div>
      <label>{{ t('orderView.assignDeliveryAddressTo', {
        count: assignCount
      }) }}</label>
      <div class="order-address__editor-deliveries neat-scroll">
        <ToggleButton
          class="order-address__editor-delivery"
          v-model="assignToInvoiceRecipient"
        >
          {{ t('orderView.invoiceRecipient') }}
        </ToggleButton>
        <ToggleButton
          class="order-address__editor-delivery"
          v-for="assign in assignTo"
          :key="assign.delivery.deliveryId"
          v-model="assign.assign"
          :disabled="assign.delivery.deliveryState !== 'open'"
        >
          {{ assign.delivery.deliveryNumber ?? t('orderView.delivery.newDelivery') }}
        </ToggleButton>
      </div>
    </div>
  </div>

  <div class="order-address__editor-actions">
    <button
      class="btn-primary"
      :disabled="disableSave"
      @click="save"
    >{{ t('common.save') }}</button>
  </div>
</template>

<style scoped>
.order-address__editor-container {
  display: grid;
  gap: 10px;
  grid-template-columns: minmax(300px, 320px) minmax(200px, max-content);
}

@media (max-width: 768px) {
  .order-address__editor-container {
    display: flex;
    flex-direction: column;
  }
}

.order-address__editor-deliveries {
  display: grid;
  grid-auto-flow: row;
  grid-auto-rows: min-content;
  gap: 5px;
  max-height: 200px;
  overflow: hidden;
  padding-bottom: 5px;
}

.order-address__editor-delivery {
  justify-content: flex-start;
}

.order-address__editor-actions {
  margin-top: 10px;
  display: flex;
  justify-content: flex-end;
}
</style>